import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    IconButton,
    InputAdornment,
    Button,
    Box,
    Typography
} from "@mui/material";
import { Delete, Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation, ViewContext } from "components/lib";
import axios from "axios";

/**
 * Reusable API Key Modal
 * @param {boolean} open - Controls dialog visibility
 * @param {string} apiKey - Current API key value
 * @param {function} setApiKey - State setter for API key
 * @param {function} onSave - Called when user clicks Save & Generate
 * @param {function} onGenerate - Called when user clicks Generate
 * @param {function} onClose - Called when user clicks Cancel or closes dialog
 * @param {boolean} isSaved - Determines if the checkbox should be pre-checked
 * @param {function} setIsSaved - State setter for save preference
 */
export const APIKeyModal = ({
    open,
    apiKey,
    setApiKey,
    onClose,
}) => {
    const { t } = useTranslation();
    const [saveApiKey, setSaveApiKey] = useState(false);
    const [deleteKey, setDeleteKey] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const viewContext = useContext(ViewContext);

    const handleConnect = async () => {
        if (!apiKey.trim()) return;

        try {
            const trimmedKey = apiKey.trim();

            if (!trimmedKey) {
                viewContext.notification.show(t(`Components.ProjectCommon.aicontent.keyError`), 'error');
                return;
            }

            if (saveApiKey) {
                const options = {
                    method: "PATCH",
                    url: "/lab/report/apikey",
                    headers: {
                        "Accept": "application/json",
                    },
                    data: { apiKey: trimmedKey },
                };

                await axios.request(options);
            }

            sessionStorage.setItem("apiKey", trimmedKey);
            setApiKey(trimmedKey);
            onClose();

            viewContext.notification.show(t(`Components.ProjectCommon.aicontent.keySaved`), 'success');
            return true;
        } catch (err) {
            viewContext.notification.show(t(`Components.ProjectCommon.aicontent.error`), 'error');
            return false;
        }
    };

    const handleDelete = async () => {
        try {
            const options = {
                method: "PATCH",
                url: "/lab/report/apikey",
                headers: {
                    "Accept": "application/json",
                },
                data: {
                    isDelete: true,
                },
            };

            const response = await axios.request(options);

            if (!response?.data?.message) {
                viewContext.notification.show(t("Components.APIKeyModal.deleteConfirm.failure"),
                    'error'
                );
                return;
            }

            sessionStorage.removeItem("apiKey");

            setApiKey("");
            setSaveApiKey(false);
            setDeleteKey(false);
            onClose();
            viewContext.notification.show(t(`${response?.data?.message}`),
                'success'
            );
        } catch (error) {
            console.error("Error deleting API key:", error);
            viewContext.notification.show(t("Components.APIKeyModal.deleteConfirm.failure"),
                'error'
            );
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                <DialogTitle>
                    {viewContext.t("Components.APIKeyModal.key.title")}
                </DialogTitle>
                {!deleteKey && (
                    <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        onClick={() => setDeleteKey(true)}
                        sx={{ mr: 2, py: 0.4, px: 1.2 }}
                        startIcon={<Delete />}
                        disabled={!apiKey && !saveApiKey}
                    >
                        {viewContext.t("Components.APIKeyModal.button.delete")}
                    </Button>
                )}
            </Box>

            {deleteKey && (
                <Box
                    sx={{
                        backgroundColor: "#fff3f3",
                        border: "1px solid #f5c2c2",
                        borderRadius: "6px",
                        m: 2,
                        mt: 1,
                        p: 2
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {viewContext.t("Components.APIKeyModal.deleteConfirm.title")}
                    </Typography>
                    <p>
                        {viewContext.t("Components.APIKeyModal.deleteConfirm.deleteConfirm")}
                    </p>

                    <Box
                        sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                    >
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => setDeleteKey(false)}
                        >
                            {viewContext.t("Components.APIKeyModal.button.cancel")}
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            variant="contained"
                            onClick={handleDelete}
                        >
                            {viewContext.t("Components.APIKeyModal.button.delete")}
                        </Button>
                    </Box>
                </Box>
            )}

            <DialogContent>
                <Typography>
                    {viewContext.t("Components.APIKeyModal.key.desc")}
                </Typography>

                <TextField
                    label={viewContext.t("Components.APIKeyModal.testField.label")}
                    variant="outlined"
                    fullWidth
                    type={showKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    sx={{ mt: 2 }}
                    helperText={viewContext.t("Components.APIKeyModal.testField.helperText")}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowKey(!showKey)} edge="end">
                                    {showKey ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={saveApiKey}
                            onChange={(e) => setSaveApiKey(e.target.checked)}
                        />
                    }
                    label={viewContext.t("Components.APIKeyModal.formControll.label")}
                    sx={{ mt: 1 }}
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    {viewContext.t("Components.APIKeyModal.button.cancel")}
                </Button>
                <Button variant="contained" disabled={!apiKey} onClick={handleConnect}>
                    {viewContext.t("Components.APIKeyModal.button.connect")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};